import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import axios from '../../axios/axiosconf';
import './SuperadminStyles.css';

const UsuarioForm = () => {
    const history = useHistory();
    const { id, clienteId } = useParams();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        client_id: clienteId || '',
        nombre: '',
        apellido: '',
        email: '',
        login: '',
        password: '',
        documento: '',
        telefono: '',
        especialidad_id: '',
        cargo_id: '',
        tipo_contrato_id: '',
        rol: 'doctor',
        activo: 1
    });
    const [clientes, setClientes] = useState([]);
    const [especialidades, setEspecialidades] = useState([]);
    const [cargos, setCargos] = useState([]);
    const [tiposContrato, setTiposContrato] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchInitialData();
    }, [history, id, isEditMode]);

    const fetchInitialData = async () => {
        try {
            const [clientesRes, especialidadesRes, cargosRes, tiposContratoRes] = await Promise.all([
                axios.get('/superadmin/clientes'),
                axios.get('/superadmin/especialidades'),
                axios.get('/superadmin/cargos'),
                axios.get('/superadmin/tipos-contrato')
            ]);

            setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
            setEspecialidades(Array.isArray(especialidadesRes.data) ? especialidadesRes.data : []);
            setCargos(Array.isArray(cargosRes.data) ? cargosRes.data : []);
            setTiposContrato(Array.isArray(tiposContratoRes.data) ? tiposContratoRes.data : []);

            if (isEditMode) {
                await fetchUsuario();
            } else if (clienteId) {
                const clientesArray = Array.isArray(clientesRes.data) ? clientesRes.data : [];
                const cliente = clientesArray.find(c => c.id === parseInt(clienteId));
                setClienteSeleccionado(cliente);
            }

            setLoading(false);
        } catch (error) {
            console.error('Error fetching initial data:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const fetchUsuario = async () => {
        try {
            // 🔧 CORRECCIÓN: Usar la nueva ruta sin clienteId
            const response = await axios.get(`/superadmin/usuarios/${id}`);
            const usuario = response.data;
            setFormData({
                client_id: usuario.client_id || '',
                nombre: usuario.name || '', // 🔧 Cambiar 'nombre' por 'name'
                apellido: usuario.lastname || '', // 🔧 Cambiar 'apellido' por 'lastname'
                email: usuario.email || '',
                login: usuario.login || '',
                password: '',
                documento: usuario.numero_documento || '', // 🔧 Cambiar 'documento' por 'numero_documento'
                telefono: usuario.celular || '', // 🔧 Cambiar 'telefono' por 'celular'
                especialidad_id: usuario.especialidad || '', // 🔧 Cambiar 'especialidad_id' por 'especialidad'
                cargo_id: usuario.cargo || '', // 🔧 Cambiar 'cargo_id' por 'cargo'
                tipo_contrato_id: usuario.tipo_contrato || '', // 🔧 Cambiar 'tipo_contrato_id' por 'tipo_contrato'
                rol: usuario.rol || 'doctor',
                activo: usuario.activo
            });
            
            const cliente = clientes.find(c => c.id === usuario.client_id);
            setClienteSeleccionado(cliente);
        } catch (error) {
            console.error('Error fetching usuario:', error);
            setErrors({ general: 'Error al cargar el usuario' });
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const newValue = type === 'number' ? parseInt(value) || '' : value;
        
        setFormData({
            ...formData,
            [name]: newValue
        });

        if (name === 'client_id') {
            const cliente = clientes.find(c => c.id === parseInt(value));
            setClienteSeleccionado(cliente);
        }

        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.client_id) {
            newErrors.client_id = 'El cliente es requerido';
        }

        if (!formData.nombre.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.apellido.trim()) {
            newErrors.apellido = 'El apellido es requerido';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'El email no tiene un formato válido';
        }

        if (!formData.login.trim()) {
            newErrors.login = 'El login es requerido';
        }

        if (!isEditMode && !formData.password.trim()) {
            newErrors.password = 'La contraseña es requerida';
        } else if (!isEditMode && formData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }

        if (!formData.documento.trim()) {
            newErrors.documento = 'El documento es requerido';
        }

        if (!isEditMode && clienteSeleccionado) {
            const usuariosActuales = clienteSeleccionado.usuarios_actuales || 0;
            if (usuariosActuales >= clienteSeleccionado.limite_usuarios) {
                newErrors.client_id = 'El cliente ha alcanzado su límite de usuarios';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            // 🔧 MAPEAR los datos del formulario a los nombres que espera el backend
            const dataToSend = {
                nombre: formData.nombre,
                apellido: formData.apellido,
                email: formData.email,
                documento: formData.documento,
                telefono: formData.telefono,
                especialidad_id: formData.especialidad_id || null,
                cargo_id: formData.cargo_id || null,
                tipo_contrato_id: formData.tipo_contrato_id || null,
                rol_id: 1, // 🔧 Agregar rol_id requerido (1 = Administrador por defecto)
                estado: formData.activo
            };

            let response;
            if (isEditMode) {
                response = await axios.put(`/superadmin/usuarios/${id}`, dataToSend);
                alert('Usuario actualizado exitosamente');
                history.push(`/superadmin/clientes/${formData.client_id}/usuarios`);
            } else {
                // Para crear, agregar campos adicionales
                dataToSend.client_id = formData.client_id;
                dataToSend.login = formData.login;
                dataToSend.password = formData.password;
                
                response = await axios.post(`/superadmin/usuarios`, dataToSend);
                if (response.data.password_temporal) {
                    setGeneratedPassword(response.data.password_temporal);
                } else {
                    alert('Usuario creado exitosamente');
                    history.push(`/superadmin/clientes/${formData.client_id}/usuarios`);
                }
            }
            setSubmitting(false);
        } catch (error) {
            console.error('Error saving usuario:', error);
            if (error.response && error.response.data.errors) {
                setErrors(error.response.data.errors);
            } else {
                setErrors({
                    general: error.response?.data?.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el usuario`
                });
            }
            setSubmitting(false);
        }
    };

    const limiteAlcanzado = clienteSeleccionado && 
                           (clienteSeleccionado.usuarios_actuales || 0) >= clienteSeleccionado.limite_usuarios;

    if (loading) {
        return (
            <SuperadminLayout>
                <div className="loading-container">
                    <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </SuperadminLayout>
        );
    }

    if (generatedPassword) {
        return (
            <SuperadminLayout>
                <div className="alert alert-success">
                    <h4><i className="fas fa-check-circle"></i> Usuario creado exitosamente</h4>
                    <hr />
                    <h5>Contraseña temporal generada:</h5>
                    <h3 className="text-center my-4">
                        <code style={{ fontSize: '1.5rem', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                            {generatedPassword}
                        </code>
                    </h3>
                    <p className="text-danger">
                        <i className="fas fa-exclamation-triangle"></i>{' '}
                        <strong>IMPORTANTE:</strong> Guarde esta contraseña. No se volverá a mostrar.
                        El usuario deberá cambiarla en su primer inicio de sesión.
                    </p>
                </div>

                <div className="text-center mt-4">
                    <Link
                        to={`/superadmin/clientes/${formData.client_id}/usuarios`}
                        className="btn btn-superadmin btn-lg"
                    >
                        <i className="fas fa-arrow-left"></i> Volver a Usuarios
                    </Link>
                </div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>
                    <i className="fas fa-user"></i> {isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h4>
                <Link
                    to={clienteId ? `/superadmin/clientes/${clienteId}/usuarios` : '/superadmin/clientes'}
                    className="btn btn-secondary"
                >
                    <i className="fas fa-arrow-left"></i> Volver
                </Link>
            </div>

            {errors.general && (
                <div className="alert alert-danger">
                    {errors.general}
                </div>
            )}

            {!isEditMode && limiteAlcanzado && (
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle"></i> El cliente seleccionado ha alcanzado su límite de usuarios.
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h5>Información del Cliente</h5>
                    
                    <div className="form-group">
                        <label htmlFor="client_id">Cliente <span className="text-danger">*</span></label>
                        <select
                            className={`form-control ${errors.client_id ? 'is-invalid' : ''}`}
                            id="client_id"
                            name="client_id"
                            value={formData.client_id}
                            onChange={handleChange}
                            disabled={isEditMode || !!clienteId}
                        >
                            <option value="">Seleccione un cliente</option>
                            {clientes.map(cliente => (
                                <option key={cliente.id} value={cliente.id}>
                                    {cliente.nombre} - {cliente.nit} 
                                    ({cliente.usuarios_actuales || 0}/{cliente.limite_usuarios} usuarios)
                                </option>
                            ))}
                        </select>
                        {errors.client_id && (
                            <div className="invalid-feedback">{errors.client_id}</div>
                        )}
                        {clienteSeleccionado && (
                            <small className="form-text text-muted">
                                Usuarios actuales: {clienteSeleccionado.usuarios_actuales || 0} de {clienteSeleccionado.limite_usuarios}
                            </small>
                        )}
                    </div>
                </div>

                <div className="form-section">
                    <h5>Información Personal</h5>
                    
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="nombre">Nombre <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                    id="nombre"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    placeholder="Nombre del usuario"
                                />
                                {errors.nombre && (
                                    <div className="invalid-feedback">{errors.nombre}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="apellido">Apellido <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.apellido ? 'is-invalid' : ''}`}
                                    id="apellido"
                                    name="apellido"
                                    value={formData.apellido}
                                    onChange={handleChange}
                                    placeholder="Apellido del usuario"
                                />
                                {errors.apellido && (
                                    <div className="invalid-feedback">{errors.apellido}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="email">Email <span className="text-danger">*</span></label>
                                <input
                                    type="email"
                                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                />
                                {errors.email && (
                                    <div className="invalid-feedback">{errors.email}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="documento">Documento <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.documento ? 'is-invalid' : ''}`}
                                    id="documento"
                                    name="documento"
                                    value={formData.documento}
                                    onChange={handleChange}
                                    placeholder="Número de documento"
                                />
                                {errors.documento && (
                                    <div className="invalid-feedback">{errors.documento}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="telefono">Teléfono</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="telefono"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono de contacto"
                                />
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="login">Login <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.login ? 'is-invalid' : ''}`}
                                    id="login"
                                    name="login"
                                    value={formData.login}
                                    onChange={handleChange}
                                    placeholder="Usuario para iniciar sesión"
                                />
                                {errors.login && (
                                    <div className="invalid-feedback">{errors.login}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="password">
                                    Contraseña {!isEditMode && <span className="text-danger">*</span>}
                                </label>
                                <input
                                    type="password"
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                    id="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder={isEditMode ? 'Dejar en blanco para no cambiar' : 'Contraseña temporal'}
                                />
                                {errors.password && (
                                    <div className="invalid-feedback">{errors.password}</div>
                                )}
                                {!isEditMode && (
                                    <small className="form-text text-muted">
                                        Mínimo 6 caracteres
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Información Profesional</h5>
                    
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="especialidad_id">Especialidad</label>
                                <select
                                    className="form-control"
                                    id="especialidad_id"
                                    name="especialidad_id"
                                    value={formData.especialidad_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione especialidad</option>
                                    {especialidades.map(esp => (
                                        <option key={esp.id} value={esp.id}>
                                            {esp.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="cargo_id">Cargo</label>
                                <select
                                    className="form-control"
                                    id="cargo_id"
                                    name="cargo_id"
                                    value={formData.cargo_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione cargo</option>
                                    {cargos.map(cargo => (
                                        <option key={cargo.id} value={cargo.id}>
                                            {cargo.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="col-md-4">
                            <div className="form-group">
                                <label htmlFor="tipo_contrato_id">Tipo de Contrato</label>
                                <select
                                    className="form-control"
                                    id="tipo_contrato_id"
                                    name="tipo_contrato_id"
                                    value={formData.tipo_contrato_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione tipo de contrato</option>
                                    {tiposContrato.map(tipo => (
                                        <option key={tipo.id} value={tipo.id}>
                                            {tipo.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h5>Configuración de Acceso</h5>
                    
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="rol">Rol</label>
                                <select
                                    className="form-control"
                                    id="rol"
                                    name="rol"
                                    value={formData.rol}
                                    onChange={handleChange}
                                >
                                    <option value="admin">Admin</option>
                                    <option value="doctor">Doctor</option>
                                    <option value="asistente">Asistente</option>
                                </select>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="activo">Estado</label>
                                <select
                                    className="form-control"
                                    id="activo"
                                    name="activo"
                                    value={formData.activo}
                                    onChange={handleChange}
                                >
                                    <option value={1}>Activo</option>
                                    <option value={0}>Inactivo</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-group mt-4">
                    <button type="submit" className="btn btn-superadmin" disabled={submitting || (!isEditMode && limiteAlcanzado)}>
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm mr-2"></span>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save"></i> {isEditMode ? 'Actualizar Usuario' : 'Crear Usuario'}
                            </>
                        )}
                    </button>
                    <Link
                        to={clienteId ? `/superadmin/clientes/${clienteId}/usuarios` : '/superadmin/clientes'}
                        className="btn btn-secondary ml-2"
                    >
                        Cancelar
                    </Link>
                </div>
            </form>
        </SuperadminLayout>
    );
};

export default UsuarioForm;
